import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X, User, Users, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * SCREEN 2: Membership Explanation — LOCKED DIRECTIVE
 * 
 * Purpose: Explain the selected membership before payment.
 * MUST ANSWER ONLY:
 * - Who it's for
 * - What it includes
 * - What it avoids
 * 
 * NO pricing, NO billing cadence, NO promo codes, NO tier comparisons.
 * Those belong on the Payment screen (Screen 3).
 */

const MEMBERSHIP_INFO = {
  individual: {
    title: 'Individual Membership',
    icon: User,
    color: '#7A8F5B',
    headline: 'For yourself.',
    description: 'A calmer way to coordinate with people who matter. No algorithms deciding what you see. No engagement tricks. Just real connection.',
    forWhom: [
      'Adults who want calmer social coordination',
      'People tired of engagement-driven platforms',
      'Anyone who values privacy and intentionality',
    ],
    includes: [
      'Base Camp — your personal dashboard',
      'Create and join groups for real activities',
      'Calendar integration without the chaos',
      'Direct messaging — no read receipts, no games',
      'Browse local activities and communities',
      'SCUTA — timeline awareness tools',
    ],
    avoids: [
      'No algorithmic feed manipulation',
      'No engagement metrics or vanity counts',
      'No ads, no data selling',
      'No pressure to post or perform',
    ],
  },
  family: {
    title: 'Family Membership',
    icon: Users,
    color: '#D98A3A',
    headline: 'For households.',
    description: 'Safe coordination for parents and children. Age-appropriate controls built in. Structure that respects everyone — not surveillance.',
    forWhom: [
      'Parents managing household schedules',
      'Families with children of any age',
      'Guardians who need visibility without intrusion',
    ],
    includes: [
      'Parent dashboard with household overview',
      'Child and teen accounts with age-appropriate access',
      'Approval workflows for activities and groups',
      'Shared family calendar',
      'Youth-to-adult conversion when they grow up',
      'All Individual membership features',
    ],
    avoids: [
      'No tracking or surveillance of children',
      'No engagement hooks targeting youth',
      'No ads shown to minors',
      'No data harvesting from family activity',
    ],
    safetyNote: 'Safety features are always free. No child is ever locked out of protection.',
  },
  institution: {
    title: 'School / Institution / Organization',
    icon: Building2,
    color: '#8B6A4F',
    headline: 'For schools, nonprofits, and programs.',
    description: 'Create a Commons for structured coordination. Dual-steward governance ensures no single point of failure. Identity is established at entry.',
    forWhom: [
      'Schools (private, homeschool, micro-school)',
      'Educational programs (after-school, tutoring)',
      'Nonprofits and community organizations',
      'Any institution requiring student safety',
    ],
    includes: [
      'Create a Commons — a shared coordination space',
      'Dual-steward governance (two people required)',
      'School calendar and scheduling tools',
      'Student Notebooks (not profiles)',
      'Parent read-only visibility',
      'Policy-governed participation',
    ],
    avoids: [
      'No student-to-student messaging',
      'No student discoverability',
      'No ads or behavioral tracking',
      'No engagement metrics or ranking',
    ],
    safetyNote: 'Child safety features are built in. This is not a social network.',
    note: 'Once entered as an institution, identity is fixed. Settings = management only.',
  },
};

export default function MembershipExplain() {
  const navigate = useNavigate();
  const { type } = useParams();
  
  const info = MEMBERSHIP_INFO[type];
  
  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--fy-text-primary)' }}>Membership type not found.</p>
          <Button onClick={() => navigate('/membership')} className="mt-4">
            Go back
          </Button>
        </div>
      </div>
    );
  }

  const Icon = info.icon;

  const handleContinue = () => {
    // Institution flow goes to institution creation, not payment
    if (type === 'institution') {
      navigate('/institutions/create');
    } else {
      navigate(`/membership/${type}/payment`);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="flex items-center p-4">
        <button
          onClick={() => navigate('/membership')}
          className="p-2 rounded-lg"
          style={{ color: 'var(--fy-text-secondary)' }}
          data-testid="back-to-intent"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      {/* Content */}
      <main className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${info.color}15` }}
            >
              <Icon className="w-8 h-8" style={{ color: info.color }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
              data-testid="membership-title"
            >
              {info.title}
            </h1>
            <p 
              className="text-lg font-medium"
              style={{ color: info.color }}
            >
              {info.headline}
            </p>
          </div>

          {/* Description */}
          <p 
            className="text-center mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            {info.description}
          </p>

          {/* WHO IT'S FOR */}
          <div className="mb-6">
            <h2 
              className="font-bold text-sm uppercase tracking-wide mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              Who it&apos;s for
            </h2>
            <ul className="space-y-2">
              {info.forWhom.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: info.color }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* WHAT IT INCLUDES */}
          <div className="mb-6">
            <h2 
              className="font-bold text-sm uppercase tracking-wide mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              What it includes
            </h2>
            <ul className="space-y-2">
              {info.includes.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: info.color }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* WHAT IT AVOIDS */}
          <div className="mb-6">
            <h2 
              className="font-bold text-sm uppercase tracking-wide mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              What it avoids
            </h2>
            <ul className="space-y-2">
              {info.avoids.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Safety note (for family) */}
          {info.safetyNote && (
            <div className="mb-6">
              <p 
                className="text-center text-xs px-4 py-3 rounded-xl"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}
              >
                {info.safetyNote}
              </p>
              {type === 'family' && (
                <p className="text-center text-xs mt-2">
                  <a 
                    href="/parent-visibility" 
                    className="underline"
                    style={{ color: 'var(--lk-sage)' }}
                  >
                    Learn about parent visibility controls →
                  </a>
                </p>
              )}
            </div>
          )}

          {/* Note (for commons) */}
          {info.note && (
            <p 
              className="text-center text-xs mb-6"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              {info.note}
            </p>
          )}

          {/* Continue button */}
          <Button
            onClick={handleContinue}
            className="w-full h-12 rounded-full font-bold"
            style={{ backgroundColor: info.color, color: '#FFFFFF' }}
            data-testid="continue-to-payment"
          >
            Continue
          </Button>

          {/* Back link */}
          <button
            onClick={() => navigate('/membership')}
            className="w-full text-center text-sm mt-4 py-2"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid="choose-different"
          >
            Choose a different option
          </button>
        </div>
      </main>
    </div>
  );
}
